/**
 * VideoRoomPanel - Active Video Room Monitor
 *
 * Displays all active video rooms from TaskRouter data with participant details.
 * Uses icons to distinguish participant roles:
 * - Doctor (staff in DR_* queue)
 * - Staff (MED_* identity, non-doctor queue)
 * - Patient (non-staff identity)
 */

import { useMemo } from 'react';
import { Card, Tag, Typography, Space, Empty, Badge, Tooltip } from 'antd';
import {
  VideoCameraOutlined,
  MedicineBoxOutlined,
  UserOutlined,
  CustomerServiceOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useUnifiedDashboardStore } from '../../store';
import usePatientNameMap from '../../hooks/usePatientNameMap';

const { Text } = Typography;

/**
 * Check if a video participant identity belongs to a staff member.
 * Staff identities follow the pattern MED_<number> (Twilio Flex worker format).
 */
const isStaffIdentity = (identity) => {
  if (!identity) return false;
  return /^MED_\d+$/i.test(identity);
};

/**
 * Check if a task queue indicates a doctor role (DR_* queues).
 */
const isDoctorQueue = (queueName) => {
  if (!queueName) return false;
  return queueName.startsWith('DR_');
};

/**
 * Format duration from a start time to now.
 */
const formatDuration = (startTime) => {
  if (!startTime) return null;
  const start = dayjs(startTime);
  if (!start.isValid()) return null;
  const diffMinutes = dayjs().diff(start, 'minute');
  if (diffMinutes < 1) return '<1m';
  if (diffMinutes < 60) return `${diffMinutes}m`;
  const hours = Math.floor(diffMinutes / 60);
  const mins = diffMinutes % 60;
  return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
};

/**
 * Classify a participant as doctor, staff, or patient.
 */
const classifyParticipant = (participant, isDoctorTask) => {
  if (isStaffIdentity(participant.identity)) {
    return isDoctorTask ? 'doctor' : 'staff';
  }
  return 'patient';
};

/**
 * Get icon and color for a participant role.
 */
const getRoleDisplay = (role) => {
  switch (role) {
    case 'doctor':
      return {
        icon: <MedicineBoxOutlined />,
        color: '#52c41a',
        label: 'Doctor',
      };
    case 'staff':
      return {
        icon: <CustomerServiceOutlined />,
        color: '#1890ff',
        label: 'Staff',
      };
    case 'patient':
    default:
      return {
        icon: <UserOutlined />,
        color: '#fa8c16',
        label: 'Patient',
      };
  }
};

const VideoRoomPanel = () => {
  const { rawTaskRouter, rawActiveCalls } = useUnifiedDashboardStore();
  const patientNameMap = usePatientNameMap(rawActiveCalls);

  // Extract active video rooms from TaskRouter tasks.
  // Multiple tasks can reference the same video room (e.g. after a task transfer
  // from PSO to DR queue). We deduplicate by roomSid and prefer the most
  // relevant task: pending/assigned/reserved > wrapping > completed.
  const videoRooms = useMemo(() => {
    const tasks = rawTaskRouter?.tasks || [];

    // Task status priority: lower = more relevant (active task wins)
    const TASK_STATUS_PRIORITY = {
      pending: 0,
      reserved: 1,
      assigned: 2,
      wrapping: 3,
      completed: 4,
    };

    // Step 1: Collect all tasks with active video rooms
    const tasksWithRooms = tasks.filter(
      (task) =>
        task.videoRoom &&
        task.videoRoom.status === 'in-progress' &&
        task.videoParticipants
    );

    // Step 2: Deduplicate by roomSid — keep the most relevant task per room
    const roomMap = new Map();
    tasksWithRooms.forEach((task) => {
      const roomSid = task.videoRoom.sid;
      const existing = roomMap.get(roomSid);
      if (!existing) {
        roomMap.set(roomSid, task);
      } else {
        // Keep the task with higher priority (lower number = more active)
        const existingPriority =
          TASK_STATUS_PRIORITY[existing.assignmentStatus] ?? 99;
        const currentPriority =
          TASK_STATUS_PRIORITY[task.assignmentStatus] ?? 99;
        if (currentPriority < existingPriority) {
          roomMap.set(roomSid, task);
        }
      }
    });

    // Step 3: Build video room entries from deduplicated tasks
    return Array.from(roomMap.values())
      .map((task) => {
        const room = task.videoRoom;
        const isDoctorTask = isDoctorQueue(task.taskQueueFriendlyName);
        const attrs = task.attributes || {};

        // Get Active Call patient names for this task
        const activeCallNames = patientNameMap.get(task.sid) || [];

        // Classify each participant and use Active Call names for patients
        let patientIndex = 0;
        const participants = (task.videoParticipants || [])
          .filter((p) => p.status === 'connected')
          .map((p) => {
            const role = classifyParticipant(p, isDoctorTask);
            const displayName =
              role === 'patient' && activeCallNames.length > 0
                ? activeCallNames[patientIndex++] || p.identity
                : p.identity;
            return {
              ...p,
              role,
              displayName,
              duration: formatDuration(p.startTime),
            };
          });

        const hasDoctor = participants.some((p) => p.role === 'doctor');
        const hasStaff = participants.some(
          (p) => p.role === 'doctor' || p.role === 'staff'
        );
        const hasPatient = participants.some((p) => p.role === 'patient');
        const count = participants.length;

        // Room status is role-based. A doctor is a MED_<n> identity assigned to
        // a DR_* (doctor) queue; patients join with their name. We distinguish a
        // genuine consultation (doctor + patient) from a room that holds people
        // but no recognised doctor — e.g. a patient with non-doctor support
        // staff, multiple patients waiting together, or a doctor who joined with
        // a non-MED_ identity we can't classify.
        const isEmpty = count === 0;
        const inConsultation = hasDoctor && hasPatient; // doctor + patient → real consult
        const patientAlone = count === 1 && hasPatient; // single patient, nobody else
        const noDoctor = count >= 2 && hasPatient && !hasDoctor; // people present, no doctor detected
        const staffAlone = hasStaff && !hasPatient; // provider(s) waiting, no patient

        return {
          roomSid: room.sid,
          roomName: room.uniqueName,
          roomStatus: room.status,
          dateCreated: room.dateCreated,
          duration: formatDuration(room.dateCreated),
          taskSid: task.sid,
          taskStatus: task.assignmentStatus,
          queueName: task.taskQueueFriendlyName,
          siteCode: attrs.videoSiteCode || null,
          patientName:
            patientNameMap.get(task.sid)?.join(', ') || attrs.name || null,
          triageCat: attrs.triageCat || null,
          isDoctorTask,
          participants,
          participantCount: count,
          hasDoctor,
          hasStaff,
          hasPatient,
          inConsultation,
          // Flags for attention
          patientAlone,
          noDoctor,
          staffAlone,
          isEmpty,
        };
      })
      .sort((a, b) => {
        // Rooms needing attention first:
        // patient alone > no doctor detected > staff waiting > empty > consult
        if (a.patientAlone !== b.patientAlone) return a.patientAlone ? -1 : 1;
        if (a.noDoctor !== b.noDoctor) return a.noDoctor ? -1 : 1;
        if (a.staffAlone !== b.staffAlone) return a.staffAlone ? -1 : 1;
        if (a.isEmpty !== b.isEmpty) return a.isEmpty ? -1 : 1;
        return 0;
      });
  }, [rawTaskRouter, patientNameMap]);

  // Summary counts
  const summary = useMemo(() => {
    const total = videoRooms.length;
    const patientAlone = videoRooms.filter((r) => r.patientAlone).length;
    const noDoctor = videoRooms.filter((r) => r.noDoctor).length;
    const staffAlone = videoRooms.filter((r) => r.staffAlone).length;
    const bothPresent = videoRooms.filter((r) => r.inConsultation).length;
    const emptyRooms = videoRooms.filter((r) => r.isEmpty).length;
    const totalParticipants = videoRooms.reduce(
      (sum, r) => sum + r.participantCount,
      0
    );
    return {
      total,
      patientAlone,
      noDoctor,
      staffAlone,
      bothPresent,
      emptyRooms,
      totalParticipants,
    };
  }, [videoRooms]);

  if (videoRooms.length === 0) {
    return (
      <div style={{ padding: '24px 0' }}>
        <Empty
          description="No active video rooms"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  /**
   * Get border color based on room status.
   */
  const getRoomBorderColor = (room) => {
    if (room.patientAlone) return '#ff4d4f'; // Red - patient waiting alone
    if (room.noDoctor) return '#faad14'; // Amber - people present, no doctor detected
    if (room.staffAlone) return '#fa8c16'; // Orange - staff waiting alone
    if (room.isEmpty) return '#d9d9d9'; // Grey - empty room
    return '#52c41a'; // Green - both present
  };

  /**
   * Get background color based on room status.
   */
  const getRoomBgColor = (room) => {
    if (room.patientAlone) return '#fff2f0'; // Light red
    if (room.noDoctor) return '#fffbe6'; // Light amber
    if (room.staffAlone) return '#fff7e6'; // Light orange
    if (room.isEmpty) return '#fafafa'; // Light grey
    return '#f6ffed'; // Light green
  };

  /**
   * Get triage category color.
   */
  const getTriageCatColor = (cat) => {
    if (!cat) return null;
    const catUpper = cat.toUpperCase();
    if (catUpper.includes('1')) return 'red';
    if (catUpper.includes('2')) return 'orange';
    if (catUpper.includes('3')) return 'gold';
    if (catUpper.includes('4')) return 'blue';
    if (catUpper.includes('5')) return 'green';
    return 'default';
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Summary Stats */}
      <div
        style={{
          display: 'flex',
          gap: 28,
          marginBottom: 16,
          padding: '12px 16px',
          backgroundColor: '#fafafa',
          borderRadius: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Space size={10}>
          <VideoCameraOutlined />
          <Text type="secondary" style={{ fontSize: 14 }}>
            Active Rooms:
          </Text>
          <Text strong style={{ fontSize: 14 }}>
            {summary.total}
          </Text>
        </Space>
        <Space size={10}>
          <UserOutlined />
          <Text type="secondary" style={{ fontSize: 14 }}>
            Total Participants:
          </Text>
          <Text strong style={{ fontSize: 14 }}>
            {summary.totalParticipants}
          </Text>
        </Space>
        {summary.bothPresent > 0 && (
          <Tag
            color="green"
            style={{ fontSize: 13, padding: '4px 10px', borderRadius: 999 }}
          >
            {summary.bothPresent} in consultation
          </Tag>
        )}
        {summary.patientAlone > 0 && (
          <Tag
            color="red"
            style={{ fontSize: 13, padding: '4px 10px', borderRadius: 999 }}
          >
            {summary.patientAlone} patient waiting alone
          </Tag>
        )}
        {summary.noDoctor > 0 && (
          <Tag
            color="gold"
            style={{ fontSize: 13, padding: '4px 10px', borderRadius: 999 }}
          >
            {summary.noDoctor} no doctor detected
          </Tag>
        )}
        {summary.staffAlone > 0 && (
          <Tag
            color="orange"
            style={{ fontSize: 13, padding: '4px 10px', borderRadius: 999 }}
          >
            {summary.staffAlone} staff waiting alone
          </Tag>
        )}
        {summary.emptyRooms > 0 && (
          <Tag style={{ fontSize: 13, padding: '4px 10px', borderRadius: 999 }}>
            {summary.emptyRooms} empty
          </Tag>
        )}
      </div>

      {/* Room Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 14,
          alignItems: 'start',
        }}
      >
        {videoRooms.map((room) => (
          <Card
            key={room.roomSid}
            size="small"
            style={{
              backgroundColor: getRoomBgColor(room),
              borderColor: getRoomBorderColor(room),
              borderLeftWidth: 4,
            }}
            styles={{ body: { padding: 16 } }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Room Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <div>
                  <Space size={6}>
                    <VideoCameraOutlined style={{ color: '#1890ff' }} />
                    <Text strong style={{ fontSize: 15 }}>
                      {room.siteCode || room.roomName}
                    </Text>
                  </Space>
                  {room.siteCode && room.roomName !== room.siteCode && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {room.roomName}
                      </Text>
                    </div>
                  )}
                </div>
                <Space size={6}>
                  {room.triageCat && (
                    <Tag
                      color={getTriageCatColor(room.triageCat)}
                      style={{
                        fontSize: 12,
                        margin: 0,
                        padding: '2px 8px',
                        borderRadius: 999,
                      }}
                    >
                      {room.triageCat}
                    </Tag>
                  )}
                  <Badge
                    count={room.participantCount}
                    style={{
                      backgroundColor:
                        room.participantCount > 0 ? '#1890ff' : '#d9d9d9',
                    }}
                  />
                </Space>
              </div>

              {/* Room Status Tags */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {room.patientAlone && (
                  <Tag
                    color="red"
                    style={{ fontSize: 12, margin: 0, borderRadius: 999 }}
                  >
                    Patient waiting alone
                  </Tag>
                )}
                {room.noDoctor && (
                  <Tag
                    color="gold"
                    style={{ fontSize: 12, margin: 0, borderRadius: 999 }}
                  >
                    No doctor detected
                  </Tag>
                )}
                {room.staffAlone && (
                  <Tag
                    color="orange"
                    style={{ fontSize: 12, margin: 0, borderRadius: 999 }}
                  >
                    Staff waiting - no patient
                  </Tag>
                )}
                {room.inConsultation && (
                  <Tag
                    color="green"
                    style={{ fontSize: 12, margin: 0, borderRadius: 999 }}
                  >
                    In consultation
                  </Tag>
                )}
                {room.isEmpty && (
                  <Tag style={{ fontSize: 12, margin: 0, borderRadius: 999 }}>
                    Empty room
                  </Tag>
                )}
                {room.duration && (
                  <Tooltip title="Room duration">
                    <Tag
                      icon={<ClockCircleOutlined />}
                      style={{ fontSize: 12, margin: 0, borderRadius: 999 }}
                    >
                      {room.duration}
                    </Tag>
                  </Tooltip>
                )}
              </div>

              {/* Participants */}
              {room.participants.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  {room.participants.map((p) => {
                    const roleDisplay = getRoleDisplay(p.role);
                    return (
                      <div
                        key={p.sid}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 10px',
                          backgroundColor: '#fff',
                          borderRadius: 6,
                          border: '1px solid #f0f0f0',
                        }}
                      >
                        <Space size={8}>
                          <Tooltip title={roleDisplay.label}>
                            <span
                              style={{ color: roleDisplay.color, fontSize: 16 }}
                            >
                              {roleDisplay.icon}
                            </span>
                          </Tooltip>
                          <Text style={{ fontSize: 14 }}>{p.displayName}</Text>
                          <Tag
                            color={roleDisplay.color}
                            style={{
                              fontSize: 11,
                              margin: 0,
                              padding: '0 6px',
                              borderRadius: 999,
                              lineHeight: '20px',
                            }}
                          >
                            {roleDisplay.label}
                          </Tag>
                        </Space>
                        {p.duration && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {p.duration}
                          </Text>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  No connected participants
                </Text>
              )}

              {/* Queue info */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Queue: {room.queueName}
                </Text>
                {room.patientName && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Patient: {room.patientName}
                  </Text>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default VideoRoomPanel;
